import * as React from "react"

interface CollapsibleProps {
  children: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

interface CollapsibleTriggerProps {
  children: React.ReactNode
  asChild?: boolean
  onClick?: () => void
}

interface CollapsibleContentProps {
  children: React.ReactNode
  className?: string
}

const CollapsibleContext = React.createContext<{
  open: boolean
  setOpen: (open: boolean) => void
}>({
  open: false,
  setOpen: () => {}
})

const Collapsible: React.FC<CollapsibleProps> = ({ 
  children, 
  open: controlledOpen, 
  onOpenChange 
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false)
  
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  
  const setOpen = React.useCallback((newOpen: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }, [controlledOpen, onOpenChange])

  return (
    <CollapsibleContext.Provider value={{ open, setOpen }}>
      {children}
    </CollapsibleContext.Provider>
  )
}

const CollapsibleTrigger: React.FC<CollapsibleTriggerProps> = ({ 
  children, 
  asChild = false,
  onClick 
}) => {
  const { open, setOpen } = React.useContext(CollapsibleContext)
  
  const handleClick = () => {
    setOpen(!open)
    onClick?.()
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, { 
      onClick: handleClick,
      ...children.props 
    })
  }

  return <div onClick={handleClick}>{children}</div>
}

const CollapsibleContent: React.FC<CollapsibleContentProps> = ({ children, className = '' }) => {
  const { open } = React.useContext(CollapsibleContext)
  
  if (!open) return null
  
  return <div className={className}>{children}</div>
}

export { Collapsible, CollapsibleTrigger, CollapsibleContent } 